import io from 'socket.io-client';

interface WorklogActivity {
  id: string;
  type?: 'worklog' | 'issue'; // Add type property for activity distinction
  webhookEvent: string;
  displayName: string;
  timeSpentMinutes?: number; // Make optional for issue activities
  issueId: string;
  issueKey?: string;
  issueName?: string;
  issueType?: string;
  projectKey?: string;
  projectName?: string;
  statusFrom?: string; // For issue status changes
  statusTo?: string; // For issue status changes
  assigneeDisplayName?: string; // For issue assignment
  priority?: string; // For issue priority
  timestamp: number;
  createdAt: string;
}

class WebSocketService {
  private socket: any = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private worklogActivityCallback: ((activity: any) => void) | null = null;
  private recentWorklogsCallback: ((worklogs: any[]) => void) | null = null;
  private errorCallback: ((error: string) => void) | null = null;
  private dataRefreshedCallback: ((data: any) => void) | null = null;
  private syncProgressCallback: ((data: any) => void) | null = null;
  private syncJobCallback: ((data: any) => void) | null = null;

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    // Disconnect existing socket if any
    if (this.socket) {
      this.socket.disconnect();
    }

    // Dev: REACT_APP_API_URL=http://localhost:3000 (from frontend/.env)
    // Production single-deployment: not set → same origin as the page
    const _apiUrl = process.env.REACT_APP_API_URL;
    const serverUrl = (_apiUrl && _apiUrl !== 'undefined') ? _apiUrl : window.location.origin;

    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: true,
      autoConnect: true,
      // Identify the user so the server can scope the realtime feed (user → team+self).
      auth: { token: localStorage.getItem('auth_token') || '' },
    });


    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Attach stored callbacks
      this.attachStoredCallbacks();
      
      // Recent worklogs are automatically sent by server on connection
    });

    // Default event listeners are now handled by stored callbacks

    this.socket.on('disconnect', (reason: any) => {
      this.isConnected = false;
      this.handleReconnect();
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('❌ WebSocket connection error:', error);
      this.isConnected = false;
      this.handleReconnect();
    });
  }

  private requestRecentWorklogs(): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('request_recent_worklogs');
    }
  }

  private attachStoredCallbacks(): void {
    if (!this.socket) return;
    
    
    if (this.worklogActivityCallback) {
      this.socket.on('worklog_activity', this.worklogActivityCallback);
    }
    
    if (this.recentWorklogsCallback) {
      this.socket.on('recent_worklogs', this.recentWorklogsCallback);
    }
    
    if (this.errorCallback) {
      this.socket.on('error', this.errorCallback);
    }
    
    if (this.dataRefreshedCallback) {
      this.socket.on('data_refreshed', this.dataRefreshedCallback);
    }

    if (this.syncProgressCallback) {
      this.socket.off('sync_progress');
      this.socket.on('sync_progress', this.syncProgressCallback);
    }

    if (this.syncJobCallback) {
      this.socket.off('sync_job');
      this.socket.on('sync_job', this.syncJobCallback);
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      
      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('❌ Max reconnection attempts reached');
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Listen for worklog activities
  onWorklogActivity(callback: (activity: WorklogActivity) => void): void {
    
    this.worklogActivityCallback = callback;
    if (this.socket) {
      this.socket.on('worklog_activity', callback);
    } else {
      console.warn('⚠️ Socket not available for worklog_activity listener, will attach on connect');
    }
  }

  // Listen for recent worklogs (sent on connection)
  onRecentWorklogs(callback: (worklogs: WorklogActivity[]) => void): void {
    this.recentWorklogsCallback = callback;
    if (this.socket) {
      this.socket.on('recent_worklogs', callback);
    } else {
      console.warn('⚠️ Socket not available for recent_worklogs listener, will attach on connect');
    }
  }

  // Listen for errors
  onError(callback: (error: string) => void): void {
    this.errorCallback = callback;
    if (this.socket) {
      this.socket.on('error', callback);
    } else {
      console.warn('⚠️ Socket not available for error listener, will attach on connect');
    }
  }

  // Listen for data refresh completion
  onDataRefreshed(callback: (data: any) => void): void {
    this.dataRefreshedCallback = callback;
    if (this.socket) {
      this.socket.on('data_refreshed', callback);
    } else {
      console.warn('⚠️ Socket not available for data_refreshed listener, will attach on connect');
    }
  }

  // Listen for data sync progress (Update data page). Removes any prior handler
  // first so events are never delivered to duplicate listeners.
  onSyncProgress(callback: (data: any) => void): void {
    this.syncProgressCallback = callback;
    if (this.socket) {
      this.socket.off('sync_progress');
      this.socket.on('sync_progress', callback);
    }
  }

  offSyncProgress(): void {
    this.syncProgressCallback = null;
    if (this.socket) this.socket.off('sync_progress');
  }

  // Listen for sync-job row updates (Update data "Sync jobs" table).
  onSyncJob(callback: (data: any) => void): void {
    this.syncJobCallback = callback;
    if (this.socket) {
      this.socket.off('sync_job');
      this.socket.on('sync_job', callback);
    }
  }

  offSyncJob(): void {
    this.syncJobCallback = null;
    if (this.socket) this.socket.off('sync_job');
  }

  // Remove all listeners
  removeAllListeners(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
    }
    // Clear stored callbacks
    this.worklogActivityCallback = null;
    this.recentWorklogsCallback = null;
    this.errorCallback = null;
    this.dataRefreshedCallback = null;
  }

  // Get connection status
  getConnectionStatus(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  // Get socket instance
  getSocket(): any {
    return this.socket;
  }

  // Get connection info for debugging
  getConnectionInfo() {
    return {
      connected: this.socket?.connected || false,
      id: this.socket?.id,
      transport: this.socket?.io?.engine?.transport?.name,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

const websocketService = new WebSocketService();
export default websocketService;
